import { Button, ModalV2, Badge, AnimatedContentLoader } from '@root/components'
import { useBusinessContext } from '@root/context'
import {
  fetchSubscriptionPrices,
  changeSubscription,
  IPlanOption,
  useSubscriptionInfo
} from '@root/query'
import { formatCurrency } from '@root/utils'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaDotCircle } from 'react-icons/fa'
import { FaRegCircle } from 'react-icons/fa6'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toaster from 'react-hot-toast'
import {
  ActiveLabel,
  ButtonRow,
  CardWrapper,
  ContentCol,
  HeaderRow,
  IconCol,
  PriceRow,
  StyledCard,
  UnstyledButton
} from '@root/containers/Stripe/components/SubscriptionRowCard'
import styled from 'styled-components'

interface ChangePlanModalProps {
  plan: IPlanOption | null
  selectedPrice: string | null
  isVisible: boolean
  isSubmitting: boolean
  onSelect: (price: string) => void
  onClose: () => void
  onConfirm: () => void
}

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  plan,
  selectedPrice,
  isVisible,
  isSubmitting,
  onSelect,
  onClose,
  onConfirm
}) => {
  const { t } = useTranslation()

  if (!plan) return null

  const getPriceLabel = price =>
    price.interval === 'month'
      ? t('settings.billing.pricePerMonth', {
          price: formatCurrency(
            price.amount,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            },
            price.currency.toUpperCase()
          )
        })
      : t('settings.billing.pricePerYear', {
          price: formatCurrency(
            price.amount,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            },
            price.currency.toUpperCase()
          )
        })

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={t('settings.billing.changeSubscription')}
      width={500}
      height={500}
      footer={
        <>
          <Button
            type="button"
            intent="default"
            onClick={onClose}
            disabled={!selectedPrice || isSubmitting}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            intent="primary"
            onClick={onConfirm}
            disabled={!selectedPrice || isSubmitting}
            showSpinner={isSubmitting}
          >
            {t('settings.billing.changeSubscription')}
          </Button>
        </>
      }
    >
      <CardWrapper>
        {plan.prices.map((price, idx) => (
          <UnstyledButton key={`unstyled-btn-${idx}`} onClick={() => onSelect(price.id)}>
            <StyledCard key={price.id} $isActive={selectedPrice === price.id}>
              <IconCol $isActive={selectedPrice === price.id}>
                {selectedPrice === price.id ? <FaDotCircle /> : <FaRegCircle />}
              </IconCol>
              <ContentCol>
                <HeaderRow>
                  <h4>{plan.name}</h4>
                  {price.interval === 'year' && <Badge>Säästä 20%</Badge>}
                </HeaderRow>
                <PriceRow>{getPriceLabel(price)}</PriceRow>
              </ContentCol>
            </StyledCard>
          </UnstyledButton>
        ))}
      </CardWrapper>
    </ModalV2>
  )
}

interface PlanCardProps {
  plan: IPlanOption
  active: boolean
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, active }) => {
  const { t } = useTranslation()

  const fmtCurr = (amount: number, currency?: string) =>
    formatCurrency(
      amount,
      { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      currency || 'EUR'
    )

  const monthly = plan.prices.find(p => p.interval === 'month')
  const yearly = plan.prices.find(p => p.interval === 'year')

  return (
    <StyledCard $isActive={active}>
      <IconCol $isActive={active}>{active ? <FaDotCircle /> : <FaRegCircle />}</IconCol>
      <ContentCol>
        <HeaderRow>
          <h4>{plan.name}</h4>
          {active && <ActiveLabel>{t('settings.billing.activeChoice')}</ActiveLabel>}
        </HeaderRow>
        <PriceRow>
          {monthly &&
            t('settings.billing.pricePerMonth', {
              price: fmtCurr(monthly.amount, monthly.currency.toUpperCase())
            })}
          {monthly && yearly && ` ${t('general.or')} `}
          {yearly &&
            t('settings.billing.pricePerYear', {
              price: fmtCurr(yearly.amount, yearly.currency.toUpperCase())
            })}
        </PriceRow>
        {!active && (
          <ButtonRow>
            <ChangeSubscriptionLabel>
              {t('settings.billing.changeSubscription')}
            </ChangeSubscriptionLabel>
          </ButtonRow>
        )}
      </ContentCol>
    </StyledCard>
  )
}

export const PlanList: React.FC = () => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: [slug, 'stripe', 'plans'],
    queryFn: () => fetchSubscriptionPrices({ slug })
  })
  const { data: active } = useSubscriptionInfo(slug)

  const { mutate: changeSubscriptionMutation, isPending: isLoading } = useMutation({
    mutationFn: (data: { product: string; price: string }) => changeSubscription({ slug }, data),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries()
        setModalPlan(null)
        toaster.success(t('general.success'))
      },
      onError: () => {
        toaster.error(t('general.error'))
      }
    }
  })

  const [modalPlan, setModalPlan] = useState<IPlanOption | null>(null)
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null)

  const openModal = (plan: IPlanOption) => {
    setModalPlan(plan)
    const suggestedPrice =
      active?.plan === plan.plan && active?.interval
        ? plan.prices.find(p => p.interval !== active.interval) || plan.prices[0]
        : plan.prices[0]
    setSelectedPrice(suggestedPrice?.id || null)
  }

  const handleCardKeyDown = (event: React.KeyboardEvent, plan: IPlanOption) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openModal(plan)
    }
  }

  const confirmChange = () => {
    if (modalPlan && selectedPrice) {
      changeSubscriptionMutation({ product: modalPlan.id, price: selectedPrice })
    }
  }

  return (
    <div>
      <AnimatedContentLoader isLoading={plansLoading}>
        <CardWrapper>
          {plans?.map(plan => (
            <SelectableCard
              key={plan.id}
              role="button"
              tabIndex={0}
              onClick={() => openModal(plan)}
              onKeyDown={event => handleCardKeyDown(event, plan)}
            >
              <PlanCard plan={plan} active={active?.plan === plan.plan} />
            </SelectableCard>
          ))}
          <ChangePlanModal
            plan={modalPlan}
            selectedPrice={selectedPrice}
            isVisible={!!modalPlan}
            isSubmitting={isLoading}
            onSelect={setSelectedPrice}
            onClose={() => setModalPlan(null)}
            onConfirm={confirmChange}
          />
        </CardWrapper>
      </AnimatedContentLoader>
    </div>
  )
}

const SelectableCard = styled.div`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;

  &:focus {
    outline: none;
  }
`

const ChangeSubscriptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 0.6rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  white-space: nowrap;
`
